/* JourneyFlowGraph — read-side visual for a journey doc: surfaces as nodes
 * (screen / router / task / agent), exits as labeled curved edges, END + journey
 * handoff terminals. Layout math lives in journeyLayout.ts, edge geometry
 * in edgeRouting.ts. Nodes are DRAGGABLE (view-only — positions live in
 * local state, the document is never written); FitPane scales the whole
 * graph down to the pane with an Expand toggle back to full size. */
import React, {
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    AlertTriangle,
    Bot,
    ClipboardCheck,
    Flag,
    Map as MapIcon,
    Monitor,
    Route as RouteIcon
} from 'lucide-react';
import {
    buildGraph,
    NodeKind
} from './journeyLayout';
import {
    GEdge,
    routeEdges
} from './edgeRouting';
import {
    journeyKeyMatch,
    matchWarningsToKeys,
    useDocWarnings
} from '../advisories';
import EvaluationStrip, {
    WarnBadge
} from './EvaluationStrip';
import {
    JOURNEY_LEGEND
} from './evalLegends';
import FitPane from './FitPane';
import VizMarkerDefs from './VizMarkerDefs';
import VizTooltip, {
    useVizTooltip
} from './VizTooltip';
import './visuals.css';

const KIND_ICON: Record<NodeKind, React.ComponentType<any>> = {
    screen: Monitor,
    router: RouteIcon,
    task: ClipboardCheck,
    agent: Bot,
    journey: MapIcon,
    end: Flag
};

interface DragState {
    key: string;
    sx: number;
    sy: number;
    bx: number;
    by: number;
}

type Offsets = Record<string, {
    dx: number;
    dy: number;
}>;

export default function JourneyFlowGraph({
    docText
}: {
    docText: string;
}) {
    const tipApi = useVizTooltip();
    const [hover, setHover] = useState<string | null>(null);
    // View-only drag offsets per node key — NEVER written to the doc.
    const [offsets, setOffsets] = useState<Offsets>({});
    const dragRef = useRef<DragState | null>(null);
    const scaleRef = useRef(1);

    const parsed = useMemo(() => {
        try {
            return {
                doc: JSON.parse(docText),
                err: null as string | null
            };
        } catch (e: any) {
            return {
                doc: null,
                err: e?.message || 'invalid JSON'
            };
        }
    }, [docText]);

    const base = useMemo(
        () => (parsed.doc ? buildGraph(parsed.doc) : null), [parsed.doc]);

    // Advisory overlay — debounced POST /validate/journey on the doc.
    const {
        warnings,
        checking
    } = useDocWarnings('journey', docText);
    const warnMap = useMemo(
        () => matchWarningsToKeys(warnings || [],
            base ? base.nodes.map((n) => n.key) : [], journeyKeyMatch),
        [warnings, base]);

    // New document → drop the drag adjustments.
    useEffect(() => {
        setOffsets({});
    }, [base]);

    const nodes = useMemo(() => {
        if (!base) return [];
        return base.nodes.map((n) => {
            const o = offsets[n.key];
            if (!o) return n;
            return {
                ...n,
                x: Math.max(2, n.x + o.dx),
                y: Math.max(2, n.y + o.dy)
            };
        });
    }, [base, offsets]);

    const routed = useMemo(
        () => (base ? routeEdges(nodes, base.raw) : null), [base, nodes]);

    useEffect(() => {
        if (base && base.problems.length) {
            // Surfaced inline too — this is the machine-readable trail.
            // eslint-disable-next-line no-console
            console.warn('[JourneyFlowGraph] shapes not drawn:', base.problems);
        }
    }, [base]);

    if (parsed.err) {
        return (
            <div className="aps-empty" style={{
                padding: '40px 20px'
            }}>
                <span>Cannot draw — the JSON does not parse ({parsed.err}).
                    Fix it in the JSON view.</span>
            </div>
        );
    }
    if (!base || !routed || base.nodes.length === 0) {
        return (
            <div className="aps-empty" style={{
                padding: '40px 20px'
            }}>
                <span>No surfaces to draw — this journey has an empty
                    surfaces list.</span>
            </div>
        );
    }

    const isHl = (e: GEdge) => hover != null
        && (e.from === hover || e.to === hover || e.key === hover);

    const onNodeDown = (key: string) => (ev: React.PointerEvent<HTMLDivElement>) => {
        if (ev.button !== 0) return;
        const o = offsets[key] || {
            dx: 0,
            dy: 0
        };
        dragRef.current = {
            key,
            sx: ev.clientX,
            sy: ev.clientY,
            bx: o.dx,
            by: o.dy
        };
        ev.currentTarget.setPointerCapture(ev.pointerId);
        tipApi.hide();
    };
    const onNodeMove = (ev: React.PointerEvent<HTMLDivElement>) => {
        const d = dragRef.current;
        if (!d) return;
        const s = scaleRef.current || 1;
        setOffsets((prev) => ({
            ...prev,
            [d.key]: {
                dx: d.bx + (ev.clientX - d.sx) / s,
                dy: d.by + (ev.clientY - d.sy) / s
            }
        }));
    };
    const onNodeUp = () => {
        dragRef.current = null;
    };

    return (
        <div className="aps-eval-layout">
            <div className="aps-eval-main">
                {base.problems.length > 0 && (
                    <div className="aps-viz-problems">
                        <AlertTriangle size={14} />
                        {base.problems.length} shape{base.problems.length > 1 ? 's' : ''} could
                        not be drawn: {base.problems.join(' · ')}
                    </div>
                )}
                <FitPane>
                    {(scale) => {
                        scaleRef.current = scale;
                        return (
                            <div className="aps-viz-canvas" style={{
                                width: routed.width,
                                height: routed.height
                            }}>
                                <svg width={routed.width} height={routed.height} style={{
                                    position: 'absolute',
                                    inset: 0
                                }}>
                                    <VizMarkerDefs />
                                    {routed.edges.map((e) => (
                                        <path
                                            key={e.key}
                                            d={e.path}
                                            className={'aps-viz-edge' + (isHl(e) ? ' hl' : '')}
                                            markerEnd={isHl(e) ? 'url(#apsVizArrHl)' : 'url(#apsVizArr)'}
                                        />
                                    ))}
                                </svg>
                                {routed.edges.map((e) => (
                                    <span
                                        key={e.key}
                                        className={'aps-viz-elabel' + (isHl(e) ? ' hl' : '')}
                                        style={{
                                            left: e.lx,
                                            top: e.ly
                                        }}
                                        onMouseEnter={(ev) => {
                                            setHover(e.key);
                                            tipApi.show(ev, e.tipTitle, e.tipBody);
                                        }}
                                        onMouseLeave={() => {
                                            setHover(null);
                                            tipApi.hide();
                                        }}
                                    >
                                        {e.label}
                                    </span>
                                ))}
                                {nodes.map((n) => {
                                    const Icon = KIND_ICON[n.kind];
                                    return (
                                        <div
                                            key={n.key}
                                            className={'aps-viz-node aps-viz-node-' + n.kind
                                                + (hover === n.key ? ' hl' : '')}
                                            style={{
                                                left: n.x,
                                                top: n.y,
                                                width: n.w,
                                                height: n.h
                                            }}
                                            onPointerDown={onNodeDown(n.key)}
                                            onPointerMove={onNodeMove}
                                            onPointerUp={onNodeUp}
                                            onPointerCancel={onNodeUp}
                                            onMouseEnter={(ev) => {
                                                setHover(n.key);
                                                tipApi.show(ev, n.tipTitle, n.tipBody);
                                            }}
                                            onMouseLeave={() => {
                                                setHover(null);
                                                tipApi.hide();
                                            }}
                                        >
                                            {n.entry && <span className="aps-viz-entry">entry</span>}
                                            {n.kind === 'end' ? (
                                                <>
                                                    <Icon size={13} />
                                                    {n.title}
                                                </>
                                            ) : (
                                                <>
                                                    <span className="aps-viz-node-icon"><Icon size={14} /></span>
                                                    <span className="aps-viz-node-text">
                                                        <span className="aps-viz-node-title" style={{
                                                            display: 'block'
                                                        }}>
                                                            {n.title}
                                                        </span>
                                                        <span className="aps-viz-node-sub" style={{
                                                            display: 'block'
                                                        }}>
                                                            {n.sub}
                                                        </span>
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                                {nodes.filter((n) => warnMap.has(n.key)).map((n) => (
                                    <WarnBadge
                                        key={`warn:${n.key}`}
                                        x={n.x + n.w - 14}
                                        y={n.y - 9}
                                        items={warnMap.get(n.key) || []}
                                        onEnter={(ev) => tipApi.show(ev, 'Advisories',
                                            (warnMap.get(n.key) || []).join('\n\n'))}
                                        onLeave={tipApi.hide}
                                    />
                                ))}
                            </div>
                        );
                    }}
                </FitPane>
                <VizTooltip tip={tipApi.tip} />
            </div>
            <EvaluationStrip
                warnings={warnings}
                checking={checking}
                legend={JOURNEY_LEGEND}
            />
        </div>
    );
}
